import is from '@sindresorhus/is';
import { Result } from 'true-myth';
import { z } from 'zod';

import {
    jobArchitectureInflightSchema,
    jobArchitectureOptionsResponseSchema,
    jobArchitecturePathSchema,
} from '../../../../shared/source/data-contract/job-architecture';
import { paginatedJobArchitectureHistorySchema } from '../../../../shared/source/data-contract/job-architecture-history';
import { jobProfileVersionsSchema } from '../../../../shared/source/data-contract/job-profile-versions';
import {
    levelingFrameworkSchema,
    paginatedLevelingFrameworkSchema,
} from '../../../../shared/source/data-contract/leveling-framework';
import { paginatedJobArchitectureSkillSchema, skillShortSchema } from '../../../../shared/source/data-contract/skill';
import { skillDetailsSchema } from '../../../../shared/source/data-contract/skill-details';
import { userPaginationSchema } from '../../../../shared/source/data-contract/user';
import { createCareerTrackWithTradeUnionsSchema } from '../../job-architecture/career-track';
import {
    jobArchitectureDebugInfoSchema,
    jobArchitectureFiltersResponseSchema,
    jobArchitectureMetadataSchema,
    jobArchitectureSchema,
    jobArchitectureSearchResponseSchema,
    jobArchitecturesSchema,
} from '../../job-architecture/job-architecture';
import { jobClusterWithTypeSchemaTransformed } from '../../job-architecture/job-cluster';
import { jobFamilyWithTypeSchemaTransformed } from '../../job-architecture/job-family';
import { jobLevelWithTypeSchemaTransformed } from '../../job-architecture/job-level';
import { jobRoleWithTypeSchemaTransformed } from '../../job-architecture/job-role';
import {
    competenciesSchema,
    jobProfileDescriptionGenerationResponseArraySchema,
    jobProfileLegacySchema,
} from '../../job-profile/job-profile-schema';
import { redetectSkillsWithPromptSchema } from '../../position/api/redetect-skills-request';
import { jobLevelDetailsSchema } from '../../position-management/job-level-details';
import { jobRoleDetailsSchema } from '../../position-management/job-role-details';
import { personsSchema } from '../../position-management/person';

import type {
    JobArchitectureInflight,
    JobArchitectureOptionsResponse,
    JobArchitecturePath,
} from '../../../../shared/source/data-contract/job-architecture';
import type { PaginatedJobArchitectureHistory } from '../../../../shared/source/data-contract/job-architecture-history';
import type { JobProfileVersions } from '../../../../shared/source/data-contract/job-profile-versions';
import type {
    LevelingFramework,
    PaginatedLevelingFramework,
} from '../../../../shared/source/data-contract/leveling-framework';
import type {
    CreateLevelingFrameworkPayload,
    UpdateLevelingFrameworkPayload,
} from '../../../../shared/source/data-contract/leveling-framework-payloads';
import type { PaginatedJobArchitectureSkill, SkillShort } from '../../../../shared/source/data-contract/skill';
import type { SkillDetails } from '../../../../shared/source/data-contract/skill-details';
import type { PaginatedUsers } from '../../../../shared/source/data-contract/user';
import type { HttpFetcherWithBaseUrl } from '../../../../shared/source/http-fetcher';
import type { HttpFetcherError, Pathname } from '../../../../shared/source/http-fetcher/http-fetcher';
import type { LanguageCode } from '../../../../shared/source/internationalization';
import type { Translate } from '../../../../shared/source/internationalization/translation';
import type { ValidatingHttpFetcherWithBaseUrl } from '../../../../shared/source/validating-http-fetcher';
import type { CareerTrack } from '../../job-architecture/career-track';
import type {
    JobArchitecture,
    JobArchitectureDebugInformation,
    JobArchitectureFiltersResponse,
    JobArchitectureMetadata,
    JobArchitectureSearchResponse,
} from '../../job-architecture/job-architecture';
import type { JobCluster } from '../../job-architecture/job-cluster';
import type { JobFamily } from '../../job-architecture/job-family';
import type { JobLevel, JobLevelWithReviewer } from '../../job-architecture/job-level';
import type { JobRole, JobRoleWithReviewer } from '../../job-architecture/job-role';
import type {
    Competencies,
    JobProfileComponent,
    JobProfileDescriptionGenerationResponseContent,
    JobProfileForGenerationWithPrompt,
    JobProfileLegacy,
} from '../../job-profile/job-profile-schema';
import type { RedetectSkillsRequestPayload, SkillsDetectionResult } from '../../position/api/redetect-skills-request';
import type { JobLevelDetails } from '../../position-management/job-level-details';
import type { JobRoleDetails } from '../../position-management/job-role-details';
import type { Persons } from '../../position-management/person';
import type { Maybe } from 'true-myth';
import type { SetRequired } from 'type-fest';

type JsonFetcher = ValidatingHttpFetcherWithBaseUrl;

type SkillWithRequiredId = Partial<SetRequired<SkillShort, 'id'>>;

export type AssignableReviewersItem = {
    readonly id: string;
    readonly architectureId: string;
    readonly type: 'level' | 'role';
};

export type AssignReviewerItem = JobLevelDetails | JobLevelWithReviewer | JobRoleDetails | JobRoleWithReviewer;

export type TitleAndDescription = {
    readonly description: string;
    readonly title: string;
};

const downloadLinkResponseSchema = z.object({
    downloadLink: z.string(),
});

export type DownloadLinkResponse = z.infer<typeof downloadLinkResponseSchema>;

export type UserRole = 'HIRING_MANAGER' | 'JOB_ARCHITECT' | 'RECRUITER';

export type GetUsersParams = {
    readonly paged: {
        readonly countOnly?: boolean;
        readonly page: number;
        readonly size: number;
    };
    readonly role?: UserRole;
    readonly sort?: string;
};

export type GetJobArchitectureSkillsParams = {
    readonly paged: {
        readonly countOnly?: boolean;
        readonly page: number;
        readonly size: number;
    };
    readonly searchTerm?: string;
    readonly sort?: string;
};

export type GetJobArchitectureHistoryParams = {
    readonly paged: {
        readonly countOnly?: boolean;
        readonly page: number;
        readonly size: number;
    };
};

export type GetLevelingFrameworksParams = {
    readonly paged: {
        readonly countOnly?: boolean;
        readonly page: number;
        readonly size: number;
    };
};

export type JobArchitectureFetchers = {
    readonly addCareerTrack: (args: {
        readonly careerTrack: Omit<CareerTrack, 'id' | 'trackType'>;
        readonly jobArchitectureId: string;
    }) => Promise<Result<CareerTrack, HttpFetcherError>>;
    readonly assignDefaultLevelingFramework: (args: {
        readonly jobArchitectureId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly assignReviewer: (args: {
        readonly item: AssignReviewerItem;
    }) => Promise<Result<JobLevel | JobRole, HttpFetcherError>>;
    readonly copyJobClusters: (args: {
        readonly jobClusters: NonEmptyArray<JobCluster>;
        readonly jobFamilyId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly copyJobLevels: (args: {
        readonly jobLevels: NonEmptyArray<JobLevel>;
        readonly jobRoleId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly copyJobRoles: (args: {
        readonly jobClusterId: string;
        readonly jobRoles: NonEmptyArray<JobRole>;
    }) => Promise<Result<unknown, Error>>;
    readonly createJobArchitecture: () => Promise<Result<JobArchitecture, HttpFetcherError>>;
    readonly createJobArchitectureLevelingFramework: (args: {
        readonly jobArchitectureId: string;
        readonly payload: CreateLevelingFrameworkPayload;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
    readonly createLevelingFramework: (args: {
        readonly payload: CreateLevelingFrameworkPayload;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
    readonly deleteJobArchitecture: (args: {
        readonly jobArchitectureId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly deleteJobArchitectureLevelingFramework: (args: {
        readonly frameworkId: string;
        readonly jobArchitectureId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly deleteJobClusters: (jobClusters: NonEmptyArray<JobCluster>) => Promise<Result<unknown, Error>>;
    readonly deleteJobFamilies: (jobFamilies: NonEmptyArray<JobFamily>) => Promise<Result<unknown, Error>>;
    readonly deleteJobLevels: (
        jobLevels: NonEmptyArray<Pick<JobLevel, 'architectureId' | 'id'>>,
    ) => Promise<Result<unknown, Error>>;
    readonly deleteJobRoles: (
        jobRoles: NonEmptyArray<Pick<JobRole, 'architectureId' | 'id'>>,
    ) => Promise<Result<unknown, Error>>;
    readonly deleteLevelingFramework: (args: {
        readonly frameworkId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly exportJobArchitecture: (args: {
        readonly jobArchitectureId: string;
        readonly languageCode: string;
    }) => Promise<Result<DownloadLinkResponse, HttpFetcherError>>;
    readonly exportJobLevel: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly languageCode: string;
    }) => Promise<Result<DownloadLinkResponse, HttpFetcherError>>;
    readonly exportJobRole: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly languageCode: string;
    }) => Promise<Result<DownloadLinkResponse, HttpFetcherError>>;
    readonly fetchAssignableReviewers: (args: {
        readonly abortSignal?: AbortSignal;
        readonly jobRoleOrJobLevel: AssignableReviewersItem;
        readonly searchTerm?: string;
    }) => Promise<Result<Persons, HttpFetcherError>>;
    readonly fetchCareerTracks: (
        jobArchitectureId: string,
    ) => Promise<Result<readonly CareerTrack[], HttpFetcherError>>;
    readonly fetchCompetencies: () => Promise<Result<Competencies, HttpFetcherError>>;
    readonly fetchDebugInformation: (
        item: JobCluster | JobFamily | JobLevel | JobRole,
    ) => Promise<Result<JobArchitectureDebugInformation, HttpFetcherError>>;
    readonly fetchJobArchitectureFilters: (args: {
        readonly jobArchitectureId: string;
    }) => Promise<Result<JobArchitectureFiltersResponse, HttpFetcherError>>;
    readonly fetchJobArchitectureHistory: (args: {
        readonly jobArchitectureId: string;
        readonly params: GetJobArchitectureHistoryParams;
    }) => Promise<Result<PaginatedJobArchitectureHistory, HttpFetcherError>>;
    readonly fetchJobArchitectureInflight: (
        jobArchitectureId: string,
    ) => Promise<Result<JobArchitectureInflight, HttpFetcherError>>;
    readonly fetchJobArchitectureLevelingFramework: (args: {
        readonly frameworkId: string;
        readonly jobArchitectureId: string;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
    readonly fetchJobArchitectureLevelingFrameworks: (args: {
        readonly jobArchitectureId: string;
        readonly params: GetLevelingFrameworksParams;
    }) => Promise<Result<PaginatedLevelingFramework, HttpFetcherError>>;
    readonly fetchJobArchitectureMetadata: (
        jobArchitectureId: string,
    ) => Promise<Result<JobArchitectureMetadata, HttpFetcherError>>;
    readonly fetchJobArchitectureOptions: () => Promise<Result<JobArchitectureOptionsResponse, HttpFetcherError>>;
    readonly fetchJobArchitectures: () => Promise<Result<readonly JobArchitecture[], HttpFetcherError>>;
    readonly fetchJobArchitectureSkills: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly jobArchitectureId: string;
        readonly params: GetJobArchitectureSkillsParams;
    }) => Promise<Result<PaginatedJobArchitectureSkill, HttpFetcherError>>;
    readonly fetchJobClusters: (
        architectureId: string,
        familyId: string,
    ) => Promise<Result<readonly JobCluster[], HttpFetcherError>>;
    readonly fetchJobFamilies: (architectureId: string) => Promise<Result<readonly JobFamily[], HttpFetcherError>>;
    readonly fetchJobLevels: (
        architectureId: string,
        roleId: string,
    ) => Promise<Result<readonly JobLevel[], HttpFetcherError>>;
    readonly fetchJobLevelVersions: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
    }) => Promise<Result<JobProfileVersions, HttpFetcherError>>;
    readonly fetchJobRoles: (
        architectureId: string,
        clusterId: string,
    ) => Promise<Result<readonly JobRole[], HttpFetcherError>>;
    readonly fetchJobRoleVersions: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
    }) => Promise<Result<JobProfileVersions, HttpFetcherError>>;
    readonly fetchLevelingFramework: (args: {
        readonly frameworkId: string;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
    readonly fetchLevelingFrameworks: (args: {
        readonly params: GetLevelingFrameworksParams;
    }) => Promise<Result<PaginatedLevelingFramework, HttpFetcherError>>;
    readonly fetchRelatedSkills: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly skills: readonly SkillWithRequiredId[];
    }) => Promise<Result<readonly SkillShort[], HttpFetcherError>>;
    readonly fetchSkillDetails: (args: {
        readonly language: string;
        readonly skillId: string;
    }) => Promise<Result<SkillDetails, HttpFetcherError>>;
    readonly fetchSkillsBySearchTerm: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly searchTerm: string;
    }) => Promise<Result<readonly SkillShort[], HttpFetcherError>>;
    readonly generateJobArchitectureWithAI: (args: {
        readonly jobArchitectureId: string;
        readonly type: string;
    }) => Promise<Result<unknown, Error>>;
    readonly generateJobClustersWithAI: (args: {
        readonly jobArchitectureId: string;
        readonly jobFamilyId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly generateJobFamilyWithAI: (jobArchitectureId: string) => Promise<Result<unknown, Error>>;
    readonly generateJobLevelsWithAI: (args: {
        readonly autoSelectTracks: boolean;
        readonly careerTracks: readonly string[];
        readonly createRoleCopy: boolean;
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly respectSkillsFromRole: boolean;
    }) => Promise<Result<unknown, Error>>;
    readonly generateJobRolesWithAI: (args: {
        readonly jobArchitectureId: string;
        readonly jobClusterId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly getJobArchitectureItemPath: (args: {
        readonly itemId: string;
        readonly jobArchitectureId: string;
    }) => Promise<Result<JobArchitecturePath, HttpFetcherError>>;
    readonly getUsers: (args: GetUsersParams) => Promise<Result<PaginatedUsers, HttpFetcherError>>;
    readonly moveJobClusters: (args: {
        readonly jobClusters: readonly JobCluster[];
        readonly jobFamilyId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly moveJobLevels: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevels: readonly JobLevel[];
        readonly jobRoleId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly moveJobRoles: (args: {
        readonly jobArchitectureId: string;
        readonly jobClusterId: string;
        readonly jobRoles: readonly JobRole[];
    }) => Promise<Result<unknown, Error>>;
    readonly patchJobArchitecture: (args: {
        readonly jobArchitecture: JobArchitecture;
        readonly languageCode: string;
    }) => Promise<Result<JobArchitecture, HttpFetcherError>>;
    readonly patchJobArchitectureMetadata: (args: {
        readonly jobArchitectureId: string;
        readonly metadata: Partial<JobArchitectureMetadata>;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly patchJobCluster: (args: {
        readonly jobCluster: JobCluster;
    }) => Promise<Result<JobCluster, HttpFetcherError>>;
    readonly patchJobFamily: (args: {
        readonly jobFamily: JobFamily;
        readonly languageCode: string;
    }) => Promise<Result<JobFamily, HttpFetcherError>>;
    readonly patchJobLevel: (args: {
        readonly jobLevel: JobLevel;
        readonly languageCode: string;
    }) => Promise<Result<JobLevel, HttpFetcherError>>;
    readonly patchJobRole: (args: {
        readonly jobRole: JobRole;
        readonly languageCode: string;
    }) => Promise<Result<JobRole, HttpFetcherError>>;
    readonly persistJobCluster: (args: {
        readonly jobArchitectureId: string;
        readonly languageCode: string;
        readonly payload: TitleAndDescription & { readonly parentId: string };
    }) => Promise<Result<JobCluster, HttpFetcherError>>;
    readonly persistJobFamily: (args: {
        readonly jobArchitectureId: string;
        readonly languageCode: string;
        readonly payload: TitleAndDescription;
    }) => Promise<Result<JobFamily, HttpFetcherError>>;
    readonly persistJobLevel: (args: {
        readonly jobArchitectureId: string;
        readonly languageCode: string;
        readonly payload: TitleAndDescription & { readonly parentId: string };
    }) => Promise<Result<JobLevel, HttpFetcherError>>;
    readonly persistJobRole: (args: {
        readonly jobArchitectureId: string;
        readonly languageCode: string;
        readonly payload: TitleAndDescription & { readonly parentId: string };
    }) => Promise<Result<JobRole, HttpFetcherError>>;
    readonly publishJobLevelVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly publishJobRoleVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly readJobLevel: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
    }) => Promise<Result<JobLevel, HttpFetcherError>>;
    readonly readJobLevelDetails: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly version: string | undefined;
    }) => Promise<Result<JobLevelDetails, HttpFetcherError>>;
    readonly readJobRole: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
    }) => Promise<Result<JobRole, HttpFetcherError>>;
    readonly readJobRoleDetails: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly version: string | undefined;
    }) => Promise<Result<JobRoleDetails, HttpFetcherError>>;
    readonly redetectSkills: (
        args: RedetectSkillsRequestPayload,
    ) => Promise<Result<SkillsDetectionResult, HttpFetcherError>>;
    readonly regenerateJobProfileDescription: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly additionalPrompt: Maybe<string>;
        readonly jobProfile: JobProfileComponent & JobProfileForGenerationWithPrompt;
    }) => Promise<Result<readonly JobProfileDescriptionGenerationResponseContent[], HttpFetcherError>>;
    readonly reviewJobLevelVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly reviewJobRoleVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly searchJobArchitecture: (args: {
        readonly jobArchitectureId: string;
        readonly page?: number;
        readonly reviewerId?: string;
        readonly searchTerm?: string;
        readonly size?: number;
        readonly type?: 'cluster' | 'family' | 'level' | 'role';
    }) => Promise<Result<JobArchitectureSearchResponse, HttpFetcherError>>;
    readonly unassignReviewer: (args: {
        readonly item: {
            readonly id: string;
            readonly architectureId: string;
            readonly type: 'job-level' | 'job-role';
        };
    }) => Promise<Result<JobLevel | JobRole, HttpFetcherError>>;
    readonly unreviewJobLevelVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly unreviewJobRoleVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly updateJobArchitectureLevelingFramework: (args: {
        readonly frameworkId: string;
        readonly jobArchitectureId: string;
        readonly payload: UpdateLevelingFrameworkPayload;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
    readonly updateJobProfile: (args: {
        readonly acceptLanguage: string;
        readonly jobProfile: JobProfileLegacy;
    }) => Promise<Result<JobProfileLegacy, HttpFetcherError>>;
    readonly updateLevelingFramework: (args: {
        readonly frameworkId: string;
        readonly payload: UpdateLevelingFrameworkPayload;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
};

export type JobArchitectureFetchersDependencies = {
    readonly cobrainerApiFetcher: HttpFetcherWithBaseUrl;
    readonly translate: Translate;
    readonly validatingCobrainerApiFetcher: JsonFetcher;
};

export const createJobArchitectureFetchers = (
    dependencies: JobArchitectureFetchersDependencies,
): JobArchitectureFetchers => {
    const { cobrainerApiFetcher, translate, validatingCobrainerApiFetcher } = dependencies;

    const generateWithAiRequestTimeout = 60_000;

    const careerTrackWithTradeUnionsSchema = createCareerTrackWithTradeUnionsSchema(translate);
    const addCareerTrack: JobArchitectureFetchers['addCareerTrack'] = async ({ careerTrack, jobArchitectureId }) => {
        return validatingCobrainerApiFetcher.postJson(careerTrackWithTradeUnionsSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/career-tracks`,
            payload: careerTrack,
        });
    };

    const assignReviewer: JobArchitectureFetchers['assignReviewer'] = async ({ item }) => {
        const payload = {
            reviewer: item.reviewer,
        };

        if ('type' in item && item.type === 'job-role') {
            return validatingCobrainerApiFetcher.patchJson(jobRoleWithTypeSchemaTransformed, {
                pathname: `/job-architectures/${item.architectureId}/roles/${item.id}`,
                payload,
            });
        }

        return validatingCobrainerApiFetcher.patchJson(jobLevelWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${item.architectureId}/levels/${item.id}`,
            payload,
        });
    };

    const unassignReviewer: JobArchitectureFetchers['unassignReviewer'] = async ({ item }) => {
        const payload = {
            reviewer: null,
        };

        if (item.type === 'job-role') {
            return validatingCobrainerApiFetcher.patchJson(jobRoleWithTypeSchemaTransformed, {
                pathname: `/job-architectures/${item.architectureId}/roles/${item.id}`,
                payload,
            });
        }

        return validatingCobrainerApiFetcher.patchJson(jobLevelWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${item.architectureId}/levels/${item.id}`,
            payload,
        });
    };

    const copyJobClusters: JobArchitectureFetchers['copyJobClusters'] = async ({ jobClusters, jobFamilyId }) => {
        try {
            const results = await Promise.all(
                jobClusters.map(({ id, architectureId }) => {
                    return cobrainerApiFetcher.post({
                        pathname: `/job-architectures/${architectureId}/clusters/${id}/copy-to/${jobFamilyId}`,
                        requestOptions: {
                            searchParams: {
                                recursive: false,
                            },
                        },
                    });
                }),
            );

            return Result.ok(results);
        } catch {
            return Result.err(new Error('failed to copy job clusters'));
        }
    };

    const copyJobLevels: JobArchitectureFetchers['copyJobLevels'] = async ({ jobLevels, jobRoleId }) => {
        try {
            const results = await Promise.all(
                jobLevels.map(({ id, architectureId }) => {
                    return cobrainerApiFetcher.post({
                        pathname: `/job-architectures/${architectureId}/levels/${id}/copy-to/${jobRoleId}`,
                    });
                }),
            );

            return Result.ok(results);
        } catch {
            return Result.err(new Error('failed to copy job levels'));
        }
    };

    const copyJobRoles: JobArchitectureFetchers['copyJobRoles'] = async ({ jobClusterId, jobRoles }) => {
        try {
            const results = await Promise.all(
                jobRoles.map(({ id, architectureId }) => {
                    return cobrainerApiFetcher.post({
                        pathname: `/job-architectures/${architectureId}/roles/${id}/copy-to/${jobClusterId}`,
                        requestOptions: {
                            searchParams: {
                                recursive: false,
                            },
                        },
                    });
                }),
            );

            return Result.ok(results);
        } catch {
            return Result.err(new Error('failed to copy job roles'));
        }
    };

    const fetchAssignableReviewers: JobArchitectureFetchers['fetchAssignableReviewers'] = async ({
        abortSignal,
        jobRoleOrJobLevel,
        searchTerm = '',
    }) => {
        const pathname: Pathname =
            jobRoleOrJobLevel.type === 'role'
                ? `/job-architectures/${jobRoleOrJobLevel.architectureId}/roles/${jobRoleOrJobLevel.id}/reviewers`
                : `/job-architectures/${jobRoleOrJobLevel.architectureId}/levels/${jobRoleOrJobLevel.id}/reviewers`;

        return validatingCobrainerApiFetcher.get(personsSchema, {
            pathname,
            requestOptions: {
                searchParams: {
                    searchTerm,
                },
                signal: abortSignal,
            },
        });
    };

    const fetchCareerTracks: JobArchitectureFetchers['fetchCareerTracks'] = async (jobArchitectureId) => {
        return validatingCobrainerApiFetcher.get(careerTrackWithTradeUnionsSchema.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/career-tracks`,
        });
    };

    const fetchCompetencies: JobArchitectureFetchers['fetchCompetencies'] = async () => {
        return validatingCobrainerApiFetcher.get(competenciesSchema, {
            pathname: '/skills/competencies',
        });
    };

    const fetchSkillDetails: JobArchitectureFetchers['fetchSkillDetails'] = async ({ language, skillId }) => {
        return validatingCobrainerApiFetcher.get(skillDetailsSchema, {
            pathname: `/skills/${skillId}/details`,
            requestOptions: {
                headers: {
                    'Accept-Language': language,
                },
            },
        });
    };

    const fetchJobLevelVersions: JobArchitectureFetchers['fetchJobLevelVersions'] = async ({
        jobArchitectureId,
        jobLevelId,
    }) => {
        return validatingCobrainerApiFetcher.get(jobProfileVersionsSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/versions`,
        });
    };

    const fetchJobRoleVersions: JobArchitectureFetchers['fetchJobRoleVersions'] = async ({
        jobArchitectureId,
        jobRoleId,
    }) => {
        return validatingCobrainerApiFetcher.get(jobProfileVersionsSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/versions`,
        });
    };

    const fetchSkillsBySearchTerm: JobArchitectureFetchers['fetchSkillsBySearchTerm'] = async ({
        acceptLanguage,
        searchTerm,
    }) => {
        return validatingCobrainerApiFetcher.get(skillShortSchema.array(), {
            pathname: `/skills`,
            requestOptions: {
                headers: {
                    'Accept-Language': acceptLanguage,
                },
                searchParams: {
                    limit: 10,
                    searchTerm,
                    type: ['hard', 'soft', 'language'].join(','),
                },
            },
        });
    };

    const fetchJobArchitectureSkills: JobArchitectureFetchers['fetchJobArchitectureSkills'] = async ({
        acceptLanguage,
        jobArchitectureId,
        params,
    }) => {
        const { paged, searchTerm, sort } = params;

        return validatingCobrainerApiFetcher.get(paginatedJobArchitectureSkillSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/skills`,
            requestOptions: {
                headers: {
                    'Accept-Language': acceptLanguage,
                },
                searchParams: {
                    countOnly: paged.countOnly,
                    page: paged.page,
                    searchTerm,
                    size: paged.size,
                    sort,
                },
            },
        });
    };

    const fetchRelatedSkills: JobArchitectureFetchers['fetchRelatedSkills'] = async ({ acceptLanguage, skills }) => {
        return validatingCobrainerApiFetcher.postJson(skillShortSchema.array(), {
            pathname: `/skills/related`,
            payload: {
                hardSkillsLimit: 10,
                languageSkillsLimit: 1,
                skills,
                softSkillsLimit: 10,
            },
            requestOptions: {
                headers: {
                    'Accept-Language': acceptLanguage,
                },
            },
        });
    };

    const fetchDebugInformation: JobArchitectureFetchers['fetchDebugInformation'] = async (item) => {
        const path = (item: JobCluster | JobFamily | JobLevel | JobRole): Pathname => {
            switch (item.type) {
                case 'job-family':
                    return `/job-architectures/${item.architectureId}/families/${item.id}/generationInfo`;
                case 'job-cluster':
                    return `/job-architectures/${item.architectureId}/clusters/${item.id}/generationInfo`;
                case 'job-role':
                    return `/job-architectures/${item.architectureId}/roles/${item.id}/generationInfo`;
                case 'job-level':
                    return `/job-architectures/${item.architectureId}/levels/${item.id}/generationInfo`;
            }
        };

        return validatingCobrainerApiFetcher.get(jobArchitectureDebugInfoSchema, {
            pathname: path(item),
        });
    };

    const createJobArchitecture: JobArchitectureFetchers['createJobArchitecture'] = async () => {
        return validatingCobrainerApiFetcher.postJson(jobArchitectureSchema, {
            pathname: '/job-architectures',
            payload: {
                translations: [],
            },
        });
    };

    const fetchJobArchitectures: JobArchitectureFetchers['fetchJobArchitectures'] = async () => {
        return validatingCobrainerApiFetcher.get(jobArchitecturesSchema, {
            pathname: '/job-architectures',
        });
    };

    const fetchJobArchitectureOptions: JobArchitectureFetchers['fetchJobArchitectureOptions'] = async () => {
        return validatingCobrainerApiFetcher.get(jobArchitectureOptionsResponseSchema, {
            pathname: '/job-architectures/options',
        });
    };

    const fetchJobArchitectureMetadata: JobArchitectureFetchers['fetchJobArchitectureMetadata'] = async (
        jobArchitectureId,
    ) => {
        return validatingCobrainerApiFetcher.get(jobArchitectureMetadataSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/metadata`,
        });
    };

    const fetchJobArchitectureInflight: JobArchitectureFetchers['fetchJobArchitectureInflight'] = async (
        jobArchitectureId,
    ) => {
        return validatingCobrainerApiFetcher.get(jobArchitectureInflightSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/inflight-tasks`,
        });
    };

    const fetchJobClusters: JobArchitectureFetchers['fetchJobClusters'] = async (jobArchitectureId, jobFamilyId) => {
        return validatingCobrainerApiFetcher.get(jobClusterWithTypeSchemaTransformed.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/families/${jobFamilyId}/clusters`,
        });
    };

    const fetchJobFamilies: JobArchitectureFetchers['fetchJobFamilies'] = async (jobArchitectureId) => {
        return validatingCobrainerApiFetcher.get(jobFamilyWithTypeSchemaTransformed.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/families`,
        });
    };

    const fetchJobRoles: JobArchitectureFetchers['fetchJobRoles'] = async (jobArchitectureId, jobClusterId) => {
        return validatingCobrainerApiFetcher.get(jobRoleWithTypeSchemaTransformed.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/clusters/${jobClusterId}/roles`,
            requestOptions: {
                searchParams: {
                    includeProfiles: false,
                },
            },
        });
    };

    const fetchJobLevels: JobArchitectureFetchers['fetchJobLevels'] = async (jobArchitectureId, jobRoleId) => {
        return validatingCobrainerApiFetcher.get(jobLevelWithTypeSchemaTransformed.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/levels`,
            requestOptions: {
                searchParams: {
                    includeProfiles: false,
                },
            },
        });
    };

    const deleteJobClusters: JobArchitectureFetchers['deleteJobClusters'] = async (jobClusters) => {
        const { architectureId } = jobClusters[0];

        return cobrainerApiFetcher.deleteJson({
            pathname: `/job-architectures/${architectureId}/clusters`,
            payload: jobClusters.map(({ id }) => id),
        });
    };

    const deleteJobFamilies: JobArchitectureFetchers['deleteJobFamilies'] = async (jobFamilies) => {
        const { architectureId } = jobFamilies[0];

        return cobrainerApiFetcher.deleteJson({
            pathname: `/job-architectures/${architectureId}/families`,
            payload: jobFamilies.map(({ id }) => id),
        });
    };

    const deleteJobLevels: JobArchitectureFetchers['deleteJobLevels'] = async (jobLevels) => {
        const { architectureId } = jobLevels[0];

        return cobrainerApiFetcher.deleteJson({
            pathname: `/job-architectures/${architectureId}/levels`,
            payload: jobLevels.map(({ id }) => id),
        });
    };

    const deleteJobRoles: JobArchitectureFetchers['deleteJobRoles'] = async (jobRoles) => {
        const { architectureId } = jobRoles[0];

        return cobrainerApiFetcher.deleteJson({
            pathname: `/job-architectures/${architectureId}/roles`,
            payload: jobRoles.map(({ id }) => id),
        });
    };

    const exportJobArchitecture: JobArchitectureFetchers['exportJobArchitecture'] = async ({
        jobArchitectureId,
        languageCode,
    }) => {
        const pathname: Pathname = `/job-architectures/${jobArchitectureId}/export`;
        const origin = window.location.origin;
        const headers = {
            'Accept-Language': languageCode,
            Origin: origin,
        };

        return validatingCobrainerApiFetcher.post(downloadLinkResponseSchema, {
            pathname,
            requestOptions: {
                headers,
                timeout: 60_000,
            },
        });
    };

    const exportJobLevel: JobArchitectureFetchers['exportJobLevel'] = async ({
        jobArchitectureId,
        jobLevelId,
        languageCode,
    }) => {
        const pathname: Pathname = `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/export`;
        const origin = window.location.origin;
        const headers = {
            'Accept-Language': languageCode,
            Origin: origin,
        };

        return validatingCobrainerApiFetcher.post(downloadLinkResponseSchema, {
            pathname,
            requestOptions: {
                headers,
            },
        });
    };

    const exportJobRole: JobArchitectureFetchers['exportJobRole'] = async ({
        jobArchitectureId,
        jobRoleId,
        languageCode,
    }) => {
        const pathname: Pathname = `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/export`;
        const origin = window.location.origin;
        const headers = {
            'Accept-Language': languageCode,
            Origin: origin,
        };

        return validatingCobrainerApiFetcher.post(downloadLinkResponseSchema, {
            pathname,
            requestOptions: {
                headers,
            },
        });
    };

    const patchJobArchitecture: JobArchitectureFetchers['patchJobArchitecture'] = async ({
        jobArchitecture,
        languageCode,
    }) => {
        const { id, title } = jobArchitecture;

        return validatingCobrainerApiFetcher.patchJson(jobArchitectureSchema, {
            pathname: `/job-architectures/${id}`,
            payload: {
                translations: [
                    {
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const patchJobArchitectureMetadata: JobArchitectureFetchers['patchJobArchitectureMetadata'] = async ({
        jobArchitectureId,
        metadata,
    }) => {
        return cobrainerApiFetcher.patchJson({
            pathname: `/job-architectures/${jobArchitectureId}/metadata`,
            payload: metadata,
        });
    };

    const patchJobFamily: JobArchitectureFetchers['patchJobFamily'] = async ({ jobFamily }) => {
        const { id, architectureId } = jobFamily;

        return validatingCobrainerApiFetcher.patchJson(jobFamilyWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${architectureId}/families/${id}`,
            payload: {
                translations: jobFamily.translations,
            },
        });
    };

    const patchJobCluster: JobArchitectureFetchers['patchJobCluster'] = async ({ jobCluster }) => {
        const { id, architectureId, translations } = jobCluster;

        return validatingCobrainerApiFetcher.patchJson(jobClusterWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${architectureId}/clusters/${id}`,
            payload: {
                translations,
            },
        });
    };

    const patchJobLevel: JobArchitectureFetchers['patchJobLevel'] = async ({ jobLevel, languageCode }) => {
        const { id, architectureId, description, title } = jobLevel;

        return validatingCobrainerApiFetcher.patchJson(jobLevelWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${architectureId}/levels/${id}`,
            payload: {
                translations: [
                    {
                        description,
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const patchJobRole: JobArchitectureFetchers['patchJobRole'] = async ({ jobRole, languageCode }) => {
        const { id, architectureId, description, title } = jobRole;

        return validatingCobrainerApiFetcher.patchJson(jobRoleWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${architectureId}/roles/${id}`,
            payload: {
                translations: [
                    {
                        description,
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const persistJobCluster: JobArchitectureFetchers['persistJobCluster'] = async ({
        jobArchitectureId,
        languageCode,
        payload,
    }) => {
        const { description, parentId, title } = payload;

        return validatingCobrainerApiFetcher.postJson(jobClusterWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/families/${parentId}/clusters`,
            payload: {
                translations: [
                    {
                        description,
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const persistJobFamily: JobArchitectureFetchers['persistJobFamily'] = async ({
        jobArchitectureId,
        languageCode,
        payload,
    }) => {
        const { description, title } = payload;

        return validatingCobrainerApiFetcher.postJson(jobFamilyWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/families`,
            payload: {
                translations: [
                    {
                        description,
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const persistJobLevel: JobArchitectureFetchers['persistJobLevel'] = async ({
        jobArchitectureId,
        languageCode,
        payload,
    }) => {
        const { description, parentId, title } = payload;

        return validatingCobrainerApiFetcher.postJson(jobLevelWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/roles/${parentId}/levels`,
            payload: {
                translations: [
                    {
                        description,
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const persistJobRole: JobArchitectureFetchers['persistJobRole'] = async ({
        jobArchitectureId,
        languageCode,
        payload,
    }) => {
        const { description, parentId, title } = payload;

        return validatingCobrainerApiFetcher.postJson(jobRoleWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/clusters/${parentId}/roles`,
            payload: {
                translations: [
                    {
                        description,
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const publishJobLevelVersion: JobArchitectureFetchers['publishJobLevelVersion'] = async ({
        jobArchitectureId,
        jobLevelId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/versions/${version}/publish`,
        });
    };

    const publishJobRoleVersion: JobArchitectureFetchers['publishJobRoleVersion'] = async ({
        jobArchitectureId,
        jobRoleId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/versions/${version}/publish`,
        });
    };

    const reviewJobLevelVersion: JobArchitectureFetchers['reviewJobLevelVersion'] = async ({
        jobArchitectureId,
        jobLevelId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/versions/${version}/review`,
        });
    };

    const reviewJobRoleVersion: JobArchitectureFetchers['reviewJobRoleVersion'] = async ({
        jobArchitectureId,
        jobRoleId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/versions/${version}/review`,
        });
    };

    const unreviewJobLevelVersion: JobArchitectureFetchers['unreviewJobLevelVersion'] = async ({
        jobArchitectureId,
        jobLevelId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/versions/${version}/unreview`,
        });
    };

    const unreviewJobRoleVersion: JobArchitectureFetchers['unreviewJobRoleVersion'] = async ({
        jobArchitectureId,
        jobRoleId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/versions/${version}/unreview`,
        });
    };

    const readJobLevel: JobArchitectureFetchers['readJobLevel'] = async ({ jobArchitectureId, jobLevelId }) => {
        return validatingCobrainerApiFetcher.get(jobLevelWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}`,
        });
    };

    const readJobRole: JobArchitectureFetchers['readJobRole'] = async ({ jobArchitectureId, jobRoleId }) => {
        return validatingCobrainerApiFetcher.get(jobRoleWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}`,
        });
    };

    const readJobLevelDetails: JobArchitectureFetchers['readJobLevelDetails'] = async ({
        jobArchitectureId,
        jobLevelId,
        version,
    }) => {
        return validatingCobrainerApiFetcher.get(jobLevelDetailsSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}?version=${version}`,
        });
    };

    const readJobRoleDetails: JobArchitectureFetchers['readJobRoleDetails'] = async ({
        jobArchitectureId,
        jobRoleId,
        version,
    }) => {
        const queryParams = new URLSearchParams();
        if (!is.undefined(version)) {
            queryParams.set('version', version);
        }

        return await validatingCobrainerApiFetcher.get(jobRoleDetailsSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}?${queryParams.toString()}`,
        });
    };

    const redetectSkills: JobArchitectureFetchers['redetectSkills'] = async ({
        additionalPrompt,
        detectionType,
        jobProfile,
        jobProfileId,
    }) => {
        const requestPayload = {
            additionalPrompt: {
                type: detectionType,
                userInput: additionalPrompt.unwrapOr(undefined),
            },
            jobProfile,
        };

        return validatingCobrainerApiFetcher.postJson(redetectSkillsWithPromptSchema, {
            pathname: `/job-profiles/${jobProfileId}/detect-skills`,
            payload: requestPayload,
            requestOptions: {
                timeout: 30_000,
            },
        });
    };

    const updateJobProfile: JobArchitectureFetchers['updateJobProfile'] = async ({ acceptLanguage, jobProfile }) => {
        return validatingCobrainerApiFetcher.patchJson(jobProfileLegacySchema, {
            pathname: `/job-profiles/${jobProfile.id}`,
            payload: jobProfile,
            requestOptions: {
                headers: {
                    'Accept-Language': acceptLanguage,
                },
            },
        });
    };

    const generateJobArchitectureWithAI: JobArchitectureFetchers['generateJobArchitectureWithAI'] = async ({
        jobArchitectureId,
        type,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/generate`,
            requestOptions: {
                searchParams: {
                    async: true,
                    type,
                },
                timeout: generateWithAiRequestTimeout,
            },
        });
    };

    const generateJobFamilyWithAI: JobArchitectureFetchers['generateJobFamilyWithAI'] = async (jobArchitectureId) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/families/generate?async=true`,
            requestOptions: {
                timeout: generateWithAiRequestTimeout,
            },
        });
    };

    const generateJobClustersWithAI: JobArchitectureFetchers['generateJobClustersWithAI'] = async ({
        jobArchitectureId,
        jobFamilyId,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/families/${jobFamilyId}/clusters/generate?async=true`,
            requestOptions: {
                timeout: generateWithAiRequestTimeout,
            },
        });
    };

    const getJobArchitectureItemPath: JobArchitectureFetchers['getJobArchitectureItemPath'] = async ({
        itemId,
        jobArchitectureId,
    }) => {
        return validatingCobrainerApiFetcher.get(jobArchitecturePathSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/items/${itemId}/path`,
        });
    };

    const getUsers: JobArchitectureFetchers['getUsers'] = async ({ paged, role, sort }) => {
        return validatingCobrainerApiFetcher.get(userPaginationSchema, {
            pathname: '/users',
            requestOptions: {
                searchParams: {
                    page: paged.page,
                    size: paged.size,
                    ...(paged.countOnly === true ? { countOnly: paged.countOnly } : {}),
                    ...(!is.nullOrUndefined(role) ? { role } : {}),
                    ...(!is.nullOrUndefined(sort) ? { sort } : {}),
                },
            },
        });
    };

    const generateJobRolesWithAI: JobArchitectureFetchers['generateJobRolesWithAI'] = async ({
        jobArchitectureId,
        jobClusterId,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/clusters/${jobClusterId}/roles/generate?async=true`,
            requestOptions: {
                timeout: generateWithAiRequestTimeout,
            },
        });
    };

    const generateJobLevelsWithAI: JobArchitectureFetchers['generateJobLevelsWithAI'] = async ({
        autoSelectTracks,
        careerTracks,
        createRoleCopy,
        jobArchitectureId,
        jobRoleId,
        respectSkillsFromRole,
    }) => {
        return cobrainerApiFetcher.postJson({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/levels/generate?async=true`,
            payload: {
                autoSelectTracks,
                careerTracks,
                createRoleCopy,
                respectSkillsFromRole,
            },
            requestOptions: {
                timeout: generateWithAiRequestTimeout,
            },
        });
    };

    const moveJobClusters: JobArchitectureFetchers['moveJobClusters'] = async ({ jobClusters, jobFamilyId }) => {
        try {
            const results = await Promise.all(
                jobClusters.map(({ id, architectureId }) => {
                    return cobrainerApiFetcher.post({
                        pathname: `/job-architectures/${architectureId}/clusters/${id}/move-to/${jobFamilyId}`,
                    });
                }),
            );

            return Result.ok(results);
        } catch {
            return Result.err(new Error('failed to move job clusters'));
        }
    };

    const moveJobLevels: JobArchitectureFetchers['moveJobLevels'] = async ({
        jobArchitectureId,
        jobLevels,
        jobRoleId,
    }) => {
        try {
            const results = await Promise.all(
                jobLevels.map(({ id }) => {
                    return cobrainerApiFetcher.post({
                        pathname: `/job-architectures/${jobArchitectureId}/levels/${id}/move-to/${jobRoleId}`,
                    });
                }),
            );

            return Result.ok(results);
        } catch {
            return Result.err(new Error('failed to move job levels'));
        }
    };

    const moveJobRoles: JobArchitectureFetchers['moveJobRoles'] = async ({
        jobArchitectureId,
        jobClusterId,
        jobRoles,
    }) => {
        try {
            const results = await Promise.all(
                jobRoles.map(({ id }) => {
                    return cobrainerApiFetcher.post({
                        pathname: `/job-architectures/${jobArchitectureId}/roles/${id}/move-to/${jobClusterId}`,
                    });
                }),
            );

            return Result.ok(results);
        } catch {
            return Result.err(new Error('failed to move job roles'));
        }
    };

    const regenerateJobProfileDescription: JobArchitectureFetchers['regenerateJobProfileDescription'] = ({
        acceptLanguage,
        additionalPrompt,
        jobProfile,
    }) => {
        const requestPayload = {
            additionalPrompt: additionalPrompt.unwrapOr(undefined),
            jobProfile,
        };

        return validatingCobrainerApiFetcher.postJson(jobProfileDescriptionGenerationResponseArraySchema, {
            pathname: `/job-profiles/${jobProfile.id}/generation`,
            payload: requestPayload,
            requestOptions: {
                headers: {
                    'Accept-Language': acceptLanguage,
                },
                timeout: 30_000,
            },
        });
    };

    const fetchJobArchitectureFilters: JobArchitectureFetchers['fetchJobArchitectureFilters'] = async ({
        jobArchitectureId,
    }) => {
        return validatingCobrainerApiFetcher.get(jobArchitectureFiltersResponseSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/filter`,
        });
    };

    const fetchJobArchitectureHistory: JobArchitectureFetchers['fetchJobArchitectureHistory'] = async ({
        jobArchitectureId,
        params,
    }) => {
        const { paged } = params;

        return validatingCobrainerApiFetcher.get(paginatedJobArchitectureHistorySchema, {
            pathname: `/job-architectures/${jobArchitectureId}/history`,
            requestOptions: {
                searchParams: {
                    page: paged.page,
                    size: paged.size,
                    ...(paged.countOnly === true ? { countOnly: paged.countOnly } : {}),
                },
            },
        });
    };

    type SearchFilterPayload =
        | { field: string; ignore_case?: boolean; op: string; value: string }
        | { filters: Array<{ field: string; ignore_case?: boolean; op: string; value: string }>; op: string };

    const buildSearchFilter = (args: {
        readonly reviewerId?: string;
        readonly searchTerm?: string;
        readonly type?: 'cluster' | 'family' | 'level' | 'role';
    }): SearchFilterPayload => {
        const { reviewerId, searchTerm, type } = args;

        const filters: Array<{ field: string; ignore_case?: boolean; op: string; value: string }> = [];

        if (is.nonEmptyString(type)) {
            filters.push({
                field: 'level',
                op: 'eq',
                value: type,
            });
        }

        if (is.nonEmptyString(reviewerId)) {
            filters.push({
                field: 'reviewerId',
                ignore_case: true,
                op: 'eq',
                value: reviewerId,
            });
        }

        if (is.nonEmptyString(searchTerm)) {
            filters.push({
                field: 'title',
                ignore_case: true,
                op: 'contains',
                value: searchTerm,
            });
        }

        if (filters.length === 1) {
            return filters[0]!;
        }

        return {
            filters,
            op: 'and',
        };
    };

    const searchJobArchitecture: JobArchitectureFetchers['searchJobArchitecture'] = async ({
        jobArchitectureId,
        page = 0,
        reviewerId,
        searchTerm,
        size = 100,
        type,
    }) => {
        const filter = buildSearchFilter({ reviewerId, searchTerm, type });

        return validatingCobrainerApiFetcher.postJson(jobArchitectureSearchResponseSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/filter`,
            payload: {
                filter,
                paged: {
                    page,
                    size,
                },
                sorted: {
                    fields: [
                        {
                            direction: 'asc',
                            field: 'levelNumber',
                        },
                    ],
                },
            },
        });
    };

    const fetchLevelingFrameworks: JobArchitectureFetchers['fetchLevelingFrameworks'] = async ({ params }) => {
        const { paged } = params;

        return validatingCobrainerApiFetcher.get(paginatedLevelingFrameworkSchema, {
            pathname: '/leveling-frameworks',
            requestOptions: {
                searchParams: {
                    countOnly: paged.countOnly,
                    page: paged.page,
                    size: paged.size,
                },
            },
        });
    };

    const createLevelingFramework: JobArchitectureFetchers['createLevelingFramework'] = async ({ payload }) => {
        return validatingCobrainerApiFetcher.postJson(levelingFrameworkSchema, {
            pathname: '/leveling-frameworks',
            payload,
        });
    };

    const fetchLevelingFramework: JobArchitectureFetchers['fetchLevelingFramework'] = async ({ frameworkId }) => {
        return validatingCobrainerApiFetcher.get(levelingFrameworkSchema, {
            pathname: `/leveling-frameworks/${frameworkId}`,
        });
    };

    const updateLevelingFramework: JobArchitectureFetchers['updateLevelingFramework'] = async ({
        frameworkId,
        payload,
    }) => {
        return validatingCobrainerApiFetcher.putJson(levelingFrameworkSchema, {
            pathname: `/leveling-frameworks/${frameworkId}`,
            payload,
        });
    };

    const deleteLevelingFramework: JobArchitectureFetchers['deleteLevelingFramework'] = async ({ frameworkId }) => {
        return cobrainerApiFetcher.delete({
            pathname: `/leveling-frameworks/${frameworkId}`,
        });
    };

    const fetchJobArchitectureLevelingFrameworks: JobArchitectureFetchers['fetchJobArchitectureLevelingFrameworks'] =
        async ({ jobArchitectureId, params }) => {
            const { paged } = params;

            return validatingCobrainerApiFetcher.get(paginatedLevelingFrameworkSchema, {
                pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks`,
                requestOptions: {
                    searchParams: {
                        countOnly: paged.countOnly,
                        page: paged.page,
                        size: paged.size,
                    },
                },
            });
        };

    const createJobArchitectureLevelingFramework: JobArchitectureFetchers['createJobArchitectureLevelingFramework'] =
        async ({ jobArchitectureId, payload }) => {
            return validatingCobrainerApiFetcher.postJson(levelingFrameworkSchema, {
                pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks`,
                payload,
            });
        };

    const fetchJobArchitectureLevelingFramework: JobArchitectureFetchers['fetchJobArchitectureLevelingFramework'] =
        async ({ frameworkId, jobArchitectureId }) => {
            return validatingCobrainerApiFetcher.get(levelingFrameworkSchema, {
                pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks/${frameworkId}`,
            });
        };

    const updateJobArchitectureLevelingFramework: JobArchitectureFetchers['updateJobArchitectureLevelingFramework'] =
        async ({ frameworkId, jobArchitectureId, payload }) => {
            return validatingCobrainerApiFetcher.putJson(levelingFrameworkSchema, {
                pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks/${frameworkId}`,
                payload,
            });
        };

    const deleteJobArchitectureLevelingFramework: JobArchitectureFetchers['deleteJobArchitectureLevelingFramework'] =
        async ({ frameworkId, jobArchitectureId }) => {
            return cobrainerApiFetcher.delete({
                pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks/${frameworkId}`,
            });
        };

    const deleteJobArchitecture: JobArchitectureFetchers['deleteJobArchitecture'] = async ({ jobArchitectureId }) => {
        return cobrainerApiFetcher.delete({
            pathname: `/job-architectures/${jobArchitectureId}`,
        });
    };

    const assignDefaultLevelingFramework: JobArchitectureFetchers['assignDefaultLevelingFramework'] = async ({
        jobArchitectureId,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks/assign-default`,
        });
    };

    return {
        addCareerTrack,
        assignDefaultLevelingFramework,
        assignReviewer,
        copyJobClusters,
        copyJobLevels,
        copyJobRoles,
        createJobArchitecture,
        createJobArchitectureLevelingFramework,
        createLevelingFramework,
        deleteJobArchitecture,
        deleteJobArchitectureLevelingFramework,
        deleteJobClusters,
        deleteJobFamilies,
        deleteJobLevels,
        deleteJobRoles,
        deleteLevelingFramework,
        exportJobArchitecture,
        exportJobLevel,
        exportJobRole,
        fetchAssignableReviewers,
        fetchCareerTracks,
        fetchCompetencies,
        fetchDebugInformation,
        fetchJobArchitectureFilters,
        fetchJobArchitectureHistory,
        fetchJobArchitectureInflight,
        fetchJobArchitectureLevelingFramework,
        fetchJobArchitectureLevelingFrameworks,
        fetchJobArchitectureMetadata,
        fetchJobArchitectureOptions,
        fetchJobArchitectures,
        fetchJobArchitectureSkills,
        fetchJobClusters,
        fetchJobFamilies,
        fetchJobLevels,
        fetchJobLevelVersions,
        fetchJobRoles,
        fetchJobRoleVersions,
        fetchLevelingFramework,
        fetchLevelingFrameworks,
        fetchRelatedSkills,
        fetchSkillDetails,
        fetchSkillsBySearchTerm,
        generateJobArchitectureWithAI,
        generateJobClustersWithAI,
        generateJobFamilyWithAI,
        generateJobLevelsWithAI,
        generateJobRolesWithAI,
        getJobArchitectureItemPath,
        getUsers,
        moveJobClusters,
        moveJobLevels,
        moveJobRoles,
        patchJobArchitecture,
        patchJobArchitectureMetadata,
        patchJobCluster,
        patchJobFamily,
        patchJobLevel,
        patchJobRole,
        persistJobCluster,
        persistJobFamily,
        persistJobLevel,
        persistJobRole,
        publishJobLevelVersion,
        publishJobRoleVersion,
        readJobLevel,
        readJobLevelDetails,
        readJobRole,
        readJobRoleDetails,
        redetectSkills,
        regenerateJobProfileDescription,
        reviewJobLevelVersion,
        reviewJobRoleVersion,
        searchJobArchitecture,
        unassignReviewer,
        unreviewJobLevelVersion,
        unreviewJobRoleVersion,
        updateJobArchitectureLevelingFramework,
        updateJobProfile,
        updateLevelingFramework,
    };
};
